import { useTranslation } from 'react-i18next';
import { Bot, CheckCircle2 } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import Modal from '../ui/Modal';
import { LEG_MODES } from '../../utils/mapConstants';
import { LEG_MODE_COLOR } from '../../utils/legFormatting';
import type { Activity } from '../../types';

export interface AiProposal {
  reordered: Activity[];
  legModes: string[];
  reasoning: string;
  benefits: string[];
}

interface Props {
  proposal: AiProposal | null;
  currentOrder: Activity[];
  onAccept: () => void;
  onDiscard: () => void;
}

export default function AiRouteProposalModal({ proposal, currentOrder, onAccept, onDiscard }: Props) {
  const { t } = useTranslation();

  return (
    <Modal open={!!proposal} onClose={onDiscard} title={t('map.aiProposalTitle')} size="sm">
      {proposal && (
        <div className="space-y-4">
          <div className="flex gap-2.5 p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800">
            <Icon as={Bot} size={16} className="text-violet-500 shrink-0 mt-0.5" />
            <p className="text-sm text-violet-800 dark:text-violet-200 leading-relaxed">{proposal.reasoning}</p>
          </div>

          {proposal.benefits.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {proposal.benefits.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 text-xs font-semibold text-teal-700 dark:text-teal-300">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                  {b}
                </span>
              ))}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">{t('map.proposedOrder')}</p>
            <ol className="space-y-1">
              {proposal.reordered.map((act, i) => {
                const legMode = i < proposal.reordered.length - 1 ? proposal.legModes[i] : null;
                const modeMeta = legMode ? LEG_MODES.find(m => m.mode === legMode) : null;
                const originalIdx = currentOrder.findIndex(a => a.id === act.id);
                return (
                  <li key={act.id} className="space-y-1">
                    <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800">
                      <span className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{act.venueName ?? act.title}</p>
                        {act.zone && <p className="text-xs text-slate-400 truncate">{act.zone}</p>}
                      </div>
                      {originalIdx !== i && (
                        <span className="ml-auto text-[10px] font-semibold text-teal-600 dark:text-teal-400 shrink-0">
                          #{originalIdx + 1} → #{i + 1}
                        </span>
                      )}
                    </div>
                    {modeMeta && (
                      <div className="flex items-center gap-1.5 pl-3 text-[11px] text-slate-500 dark:text-slate-400">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full"
                          style={{ backgroundColor: `${LEG_MODE_COLOR[modeMeta.mode]}20`, color: LEG_MODE_COLOR[modeMeta.mode] }}>
                          <Icon as={modeMeta.Icon} size={11} />
                        </span>
                        <span className="font-medium">{t(`map.travelMode.${modeMeta.mode}`)}</span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={onAccept} className="btn-primary flex-1 gap-1.5">
              <Icon as={CheckCircle2} size={14} /> {t('common.accept')}
            </button>
            <button onClick={onDiscard} className="btn-secondary">{t('common.discard')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
